<template>
  <div>
    <v-card-title class="text-h6 font-weight-bold">
      {{ $vuetify.lang.t("$vuetify.access_control.title") }}
    </v-card-title>
    <v-divider></v-divider>

    <v-card class="elevation-0">
      <v-row class="pa-4">
        <v-col cols="6" md="8">
          <v-card class="outlined" flat>
            <strong class="pt-2 d-block">
              {{ $vuetify.lang.t("$vuetify.access_control.assigned_members") }}
            </strong>
          </v-card>
        </v-col>
        <v-col cols="6" md="4">
          <v-row class="justify-end">
            <v-btn
              @click="memberRemoveConfirmationModal = true"
              class="mr-3"
              color="primary"
              :disabled="removeBtnDisable"
            >
              {{ $vuetify.lang.t("$vuetify.common.remove") }} ({{ selectedProjectMembers.length }})
            </v-btn>
            <Button
              :buttonType="FLOATING_ACTION_BUTTON"
              iconName="mdi-plus"
              size="x-small"
              :buttonText="$vuetify.lang.t('$vuetify.access_control.assign_new_member')"
              color="primary"
              :disabled="!$hasProjectAccessFor([$pRole.ADMIN]) || assignedMembersLoading"
              @onClick="addNewProjectMember()"
            />
          </v-row>
        </v-col>
      </v-row>
      <v-row class="mx-2 mb-3">
        <v-col cols="12" xs="12" md="3">
          <v-text-field
            v-model="memberSearch"
            append-icon="mdi-magnify"
            :label="$vuetify.lang.t('$vuetify.member.search')"
            outlined
            dense
            single-line
            hide-details
            @input="pagination.page = 1"
          ></v-text-field>
        </v-col>
        <v-col cols="12" xs="12" md="2">
          <v-select
            v-model="selectedRole"
            :items="memberRoleList"
            outlined
            dense
            :label="$vuetify.lang.t('$vuetify.member.role')"
            item-text="text"
            item-value="value"
            @change="pagination.page = 1"
          ></v-select>
        </v-col>
        <v-col cols="12" xs="12" md="2">
          <v-select
            v-model="selectedProjectRoleFilter"
            :items="memberRoleList"
            outlined
            dense
            :label="$vuetify.lang.t('$vuetify.member.project_role')"
            item-text="text"
            item-value="value"
            @change="pagination.page = 1"
          ></v-select>
        </v-col>
        <v-col cols="12" xs="12" md="2">
          <v-select
            v-model="selectedStatus"
            :items="memberStatusList"
            :label="$vuetify.lang.t('$vuetify.common.status')"
            outlined
            dense
            item-text="text"
            item-value="value"
            @change="pagination.page = 1"
          ></v-select>
        </v-col>
        <v-col cols="12" xs="12" md="1">
          <v-btn
            class="float-right float-md-left text-capitalize"
            color="primary"
            @click="resetFilters"
            :disabled="isMemberResetDisabled"
          >
            <v-icon left small>mdi-refresh</v-icon>
            {{ $vuetify.lang.t("$vuetify.common.reset") }}
          </v-btn>
        </v-col>
      </v-row>
      <Loader v-if="assignedMembersLoading" class="mr-2 mt-6" />
      <v-data-table
        v-else
        :header-props="{ sortIcon: 'mdi-arrow-up' }"
        must-sort
        :items="filteredProjectMembers"
        v-model="selectedProjectMembers"
        class="elevation-1 mt-6 members-table"
        :headers="projectMembersHeaders"
        outlined
        show-expand
        :show-select="this.organization?.is_account_owner"
        :search="memberSearch"
        :custom-filter="customSearch"
        :page.sync="pagination.page"
        :items-per-page.sync="pagination.itemsPerPage"
        :sort-by.sync="clientSideSortBy"
        :sort-desc.sync="clientSideSortDesc"
      >
        <template v-slot:[`item.user.first_name`]="{ item }">
          <span>{{ item.user.first_name }} {{ item.user.last_name }}</span>
        </template>
        <template v-slot:[`item.permissions.role`]="{ item }">
          <div class="text-center">
            <v-select
              class="custom-width text-uppercase d-inline-block"
              :items="
                Object.values($pRole).map(r => ({
                  text: r.toUpperCase(),
                  value: r.toLowerCase(),
                }))
              "
              :placeholder="$vuetify.lang.t('$vuetify.access_control.project_role_placeholder').toUpperCase()"
              item-text="text"
              item-value="value"
              v-model="item.permissions.role"
              dense
              outlined
              :disabled="!$hasProjectAccessFor([$pRole.ADMIN]) || $isLoggedInUser(item.user.id)"
              @focus="prevProjectRole = item.permissions.role"
              @change="onProjectRoleChange($event, item)"
            />
          </div>
        </template>
        <template #[`item.access_control`]="{ item }">
          <div class="d-flex justify-center align-center">
            <v-tooltip bottom :disabled="!shouldDisableManageAccess(item)">
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="d-inline-block">
                  <v-btn
                    v-if="isUserProjectRole(item) && $hasProjectAccessFor([$pRole.ADMIN])"
                    :disabled="shouldDisableManageAccess(item)"
                    small
                    outlined
                    color="blue-grey lighten-2"
                    class="primary--text reset-btn"
                    @click="openFeatureAccessModal(item, 'edit')"
                  >
                    {{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.manage_access") }}
                  </v-btn>
                </span>
              </template>

              {{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.manage_access_disabled_tooltip") }}
            </v-tooltip>
            <!-- <span v-else>-</span> -->
          </div>
        </template>
        <template v-slot:[`item.tags`]="{ item }">
          <div class="tags-cell">
            <div class="d-inline-flex align-center">
              <!-- Only show edit icon if project role is 'guest' and loggedin user has admin access -->
              <div>
                <v-tooltip
                  v-if="['guest', 'user'].includes(item.permissions.role) && $hasProjectAccessFor([$pRole.ADMIN])"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="mr-2" @click="editUserTags(item)" v-bind="attrs" v-on="on">
                      mdi-pencil
                    </v-icon>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
              </div>
            </div>
            <v-chip v-for="(tag, index) in item.tags.slice(0, 2)" :key="index" class="ma-1" color="primary" small dark>
              {{ tag }}
            </v-chip>
            <span class="no-wrap" v-if="item.tags.length > 2">+{{ item.tags.length - 2 }} more</span>
          </div>
        </template>
        <template v-slot:expanded-item="{ item }">
          <td :colspan="projectMembersHeaders.length">
            <v-row>
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mb-1">
                  <span class="mr-2">Tags</span>
                </div>
                <!-- <v-chip-group column> -->
                <v-chip v-for="(tag, index) in item.tags" :key="index" class="ma-1" color="primary" small dark>
                  {{ tag }}
                </v-chip>
                <!-- </v-chip-group> -->
              </v-col>
            </v-row>
          </td>
        </template>
        <template v-slot:[`item.created_at`]="{ item }">
          {{ item.created_at ? formatDateTime(item.created_at) : "N/A" }}
        </template>
      </v-data-table>
      <v-dialog v-model="newProjectMemberDialog" :width="$vuetify.breakpoint.xsOnly ? '90%' : '80%'">
        <v-card class="pa-4 pa-md-8 fixed-dialog">
          <!-- Tabs Header -->
          <v-tabs v-model="activeTab" background-color="transparent" show-arrows grow>
            <v-tabs-slider></v-tabs-slider>
            <v-tab v-if="canViewMembers">
              {{ $vuetify.lang.t("$vuetify.access_tab.assign_members") }}
            </v-tab>
            <v-tab>
              {{ $vuetify.lang.t("$vuetify.access_tab.invite_guest") }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item v-if="canViewMembers">
              <v-row>
                <v-col cols="6">
                  <div class="py-5 primary--text font-weight-bold">
                    {{ $vuetify.lang.t("$vuetify.access_control.assign_to_project") }}
                  </div>
                </v-col>
                <v-col cols="6">
                  <v-row class="d-flex justify-end">
                    <v-btn
                      @click="showMemberAssignmentConfirmationModal"
                      class="mt-4 mr-3"
                      color="primary"
                      :disabled="assignBtnDisable"
                      >{{ $vuetify.lang.t("$vuetify.access_control.assign") }} ({{ selectedMembers.length }})</v-btn
                    >
                  </v-row>
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <v-row>
                <v-col cols="12" xs="12" md="3">
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    :label="$vuetify.lang.t('$vuetify.member.search')"
                    outlined
                    dense
                    single-line
                    hide-details
                    @input="handleSearch"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" md="3">
                  <v-select
                    :items="memberRoleList"
                    item-text="text"
                    item-value="value"
                    v-model="memberRole"
                    outlined
                    dense
                    @change="handleQueryOnMemberList"
                    label="Role"
                  ></v-select>
                </v-col>
                <v-col cols="12" xs="12" md="3">
                  <v-select
                    :items="memberStatusList"
                    item-text="text"
                    item-value="value"
                    v-model="memberStatus"
                    outlined
                    dense
                    @change="handleQueryOnMemberList"
                    label="Status"
                  ></v-select>
                </v-col>
                <v-col cols="12" xs="12" md="3">
                  <v-btn
                    class="float-right float-md-left text-capitalize"
                    color="primary"
                    @click="resetAndFetchMembers"
                    :disabled="isResetDisabled"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    {{ $vuetify.lang.t("$vuetify.common.reset") }}
                  </v-btn>
                </v-col>
              </v-row>
              <Loader v-if="isLoading" class="mr-2 mt-6" />
              <v-data-table
                v-else
                :items="selectableMembers"
                v-model="selectedMembers"
                class="elevation-1 members-table"
                :header-props="{ sortIcon: 'mdi-arrow-up' }"
                :headers="headers"
                outlined
                show-select
                must-sort
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                selectable-key="selectable"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                @update:sort-by="onSortChange"
                @update:sort-desc="onSortChange"
              >
                <template v-slot:[`item.first_name`]="{ item }">
                  <span>{{ item.first_name }} {{ item.last_name }}</span>
                </template>
                <template v-slot:[`item.permissions.role`]="{ item }">
                  <div class="d-flex justify-center align-center">
                    <!-- onMemberRoleChange(item.id, $event)
                    here $event = any selected project role
                    -->
                    <v-autocomplete
                      v-model="item.permissions.role"
                      :error="membersProjectRoleErrors[item.id]"
                      :items="[...Object.values($pRole).map(r => ({ text: r.toUpperCase(), value: r.toLowerCase() }))]"
                      :placeholder="$vuetify.lang.t('$vuetify.access_control.project_role_placeholder').toUpperCase()"
                      item-text="text"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      required
                      class="flex-grow-0 flex-shrink-0"
                      style="width: 145px"
                      @input="onMemberRoleChange(item.id, $event)"
                    />
                  </div>
                </template>
                <template #[`item.access_control`]="{ item }">
                  <v-tooltip
                    v-if="item.permissions.role === 'user' && $hasProjectAccessFor([$pRole.ADMIN])"
                    bottom
                    :disabled="!shouldDisableManageAccess(item, 'assign')"
                  >
                    <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-btn
                          :disabled="shouldDisableManageAccess(item, 'assign')"
                          small
                          outlined
                          color="blue-grey lighten-2"
                          class="primary--text reset-btn"
                          @click="openFeatureAccessModal(item, 'assign')"
                        >
                          {{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.manage_access") }}
                        </v-btn>
                      </span>
                    </template>

                    {{
                      $vuetify.lang.t("$vuetify.user_access_control_permission_feature.manage_access_disabled_tooltip")
                    }}
                  </v-tooltip>

                  <!-- <span v-else>-</span> -->
                </template>
                <!-- <template v-slot:[`item.tags`]="{ item }">
                  <span v-if="item.role === 'guest'">{{ item.tags.slice(0, 3).join(",") }}{{ item.tags.length > 3 ? "..." : "" }}</span>
                </template> -->
              </v-data-table>
              <v-row>
                <v-col class="py-0" cols="12" md="12">
                  <v-divider></v-divider>
                </v-col>
                <v-col cols="12" xs="12" sm="3" md="3"></v-col>
                <v-col cols="12" xs="12" sm="6" md="6">
                  <v-pagination
                    class="text-center"
                    v-model="page"
                    :length="pageCount"
                    @input="onPageChange"
                  ></v-pagination>
                </v-col>
                <v-col cols="12" offset-sm="1" offset-md="1" xs="3" sm="2" md="2">
                  <v-select
                    v-model="itemsPerPage"
                    :items="rows"
                    hide-details="auto"
                    outlined
                    dense
                    @change="onItemsPerPageChange"
                  ></v-select>
                </v-col>
              </v-row>
            </v-tab-item>
            <v-tab-item>
              <div class="py-5 primary--text font-weight-bold ml-2">
                {{ $vuetify.lang.t("$vuetify.access_tab.invite_guest_users") }}
              </div>
              <v-form ref="guestUsersForm">
                <div v-for="(guest, index) in guests" :key="index" class="pa-4">
                  <span style="margin-left: -8px" class="primary--text mb-2 d-block font-weight-bold"
                    >{{ $vuetify.lang.t("$vuetify.access_tab.guest") }} {{ index + 1 }}</span
                  >
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="guest.email"
                        :label="$vuetify.lang.t('$vuetify.member.email')"
                        outlined
                        dense
                        :rules="[rules.required, rules.email(guests, index)]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-combobox
                        v-model="guest.tags"
                        :items="[]"
                        :label="$vuetify.lang.t('$vuetify.access_tab.tags')"
                        variant="solo"
                        clearable
                        outlined
                        multiple
                        dense
                        closable
                        :append-icon="null"
                        :hint="$vuetify.lang.t('$vuetify.gpt_profile.chip_hint')"
                        class="no-dropdown-icon"
                        :disabled="isLoading"
                        @change="val => onGuestsTagsChange(val, index)"
                      >
                        <template v-slot:selection="{ attrs, item, select, selected }">
                          <v-chip
                            v-bind="attrs"
                            small
                            :model-value="selected"
                            @click="select"
                            @click:close="removeTag(item, index)"
                            close
                          >
                            <span>{{ item }}</span
                            >&nbsp;
                          </v-chip>
                        </template>
                      </v-combobox>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="guest.first_name"
                        :label="$vuetify.lang.t('$vuetify.profile.first_name_label_txt')"
                        outlined
                        dense
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="guest.last_name"
                        :label="$vuetify.lang.t('$vuetify.profile.last_name_label_txt')"
                        outlined
                        dense
                      />
                    </v-col>
                  </v-row>
                  <v-row dense justify="end" v-if="guests.length > 1">
                    <v-col cols="auto">
                      <Button
                        :buttonType="FLOATING_ACTION_BUTTON"
                        iconName="mdi-minus"
                        size="x-small"
                        class=""
                        :buttonText="$vuetify.lang.t('$vuetify.access_tab.remove_guest')"
                        color="primary"
                        @onClick="removeGuest(index)"
                        style="margin-right: -7px"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-form>
              <div class="d-flex justify-end">
                <Button
                  :buttonType="FLOATING_ACTION_BUTTON"
                  iconName="mdi-plus"
                  size="x-small"
                  class=""
                  :buttonText="$vuetify.lang.t('$vuetify.access_tab.add_another_guest')"
                  color="primary"
                  @onClick="addGuest()"
                />
              </div>
            </v-tab-item>
          </v-tabs-items>
          <v-row>
            <v-col cols="12" class="d-flex justify-end pr-4">
              <v-btn outlined @click="closeMembersAssignmentModal" color="error">
                {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
              </v-btn>
              <v-btn
                color="primary"
                class="ml-2"
                v-if="activeTab === 1 || canViewMembers !== true"
                :loading="guestInvitationLoading"
                @click="inviteGuestMember"
              >
                {{ $vuetify.lang.t("$vuetify.access_tab.send_invitation") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>

      <v-dialog v-model="guestEditDialog" :width="$vuetify.breakpoint.xsOnly ? '90%' : '80%'">
        <v-card class="pa-8">
          <v-row>
            <v-col cols="6">
              <div class="py-4 primary--text font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.access_tab.update_guest_user") }}
              </div>
            </v-col>
          </v-row>
          <v-form ref="group_invitation_form" lazy-validation>
            <v-row>
              <v-col cols="10">
                <v-combobox
                  v-model="tags"
                  :items="[]"
                  label="Tags"
                  variant="solo"
                  clearable
                  outlined
                  multiple
                  dense
                  closable
                  :append-icon="null"
                  @change="onUserTagsChange"
                  :hint="$vuetify.lang.t('$vuetify.gpt_profile.chip_hint')"
                  class="no-dropdown-icon"
                  :disabled="isLoading"
                >
                  <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip
                      v-bind="attrs"
                      small
                      :model-value="selected"
                      @click="select"
                      @click:close="removeMembersTag(item)"
                      close
                    >
                      <span>{{ item }}</span
                      >&nbsp;
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
          </v-form>
          <v-row>
            <v-col>
              <v-card-actions class="justify-end">
                <v-btn @click="closeGuestEditModal" outlined color="error">
                  {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
                </v-btn>
                <v-btn color="primary" outlined @click="updateGuestUsersTag" :loading="isLoading">
                  {{ $vuetify.lang.t("$vuetify.common.update_btn") }}
                </v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>

      <DialogModal
        v-model="memberConfirmationModal"
        :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
        :message="confirmMessage"
        :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
        :cancelButtonText="$vuetify.lang.t('$vuetify.common.preview')"
        @onConfirmation="assignMembers"
        @onCancel="
          memberConfirmationModal = false;
          newProjectMemberDialog = true;
        "
      />
      <!-- member remove modal -->
      <DialogModal
        v-model="memberRemoveConfirmationModal"
        :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
        :message="confirmRemoveMessage"
        :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
        :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel')"
        @onConfirmation="removeProjectMembers"
        @onCancel="memberRemoveConfirmationModal = false"
      />
      <!-- project role update confirmation modal  -->
      <DialogModal
        v-model="projectRoleEditDialog"
        :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
        :message="
          isGuestToOtherProjectRoleUpdate
            ? $vuetify.lang.t('$vuetify.access_control.project_role_guest_changed', prevProjectRole.toUpperCase())
            : $vuetify.lang.t('$vuetify.access_control.project_role_confirmation')
        "
        :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
        :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel')"
        @onConfirmation="updateProjectRole"
        @onCancel="onProjectRoleCancel"
      />

      <FeatureAccessModal
        v-model="showFeatureAccessModal"
        :user-name="selectedAccessUserName"
        :feature-config="featureAccessConfig"
        :loading="isSavingFeatureAccess"
        @save="saveFeatureAccess"
        @close="closeFeatureAccessModal"
      />
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import { FLOATING_ACTION_BUTTON } from "@/components/common/button/_buttontypes";
import Button from "@/components/common/button/Button";
import DialogModal from "@/components/common/DialogModal";
import FeatureAccessModal from "@/components/common/FeatureAccessModal.vue";
import Loader from "@/components/common/loader/Loader";
import {
  ADD_ALERT,
  ASSIGN_PROJECT_MEMBERS,
  DELETE_PROJECT_MEMBERS,
  INVITE_GUEST_USERS,
  LOAD_ALL_MEMBERS,
  RETRIEVE_PROJECT_MEMBERS,
  UPDATE_PROJECT_MEMBERS,
} from "@/store/_actiontypes";
import { formatDateTime } from "@/helpers/helper";

import { mergeFeatureConfig, extractFeaturePermissions } from "@/helpers/helper";

export default {
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    Button,
    // eslint-disable-next-line vue/no-reserved-component-names
    DialogModal,
    Loader,
    FeatureAccessModal,
  },

  props: {},
  data() {
    return {
      FLOATING_ACTION_BUTTON: FLOATING_ACTION_BUTTON,
      newProjectMemberDialog: false,
      isLoading: false,
      formatDateTime,
      selectedMembers: [],
      userRoleSelections: {},
      memberConfirmationModal: false,
      memberRemoveConfirmationModal: false,
      selectedProjectMembers: [],
      membersProjectRoleErrors: {},
      assignedMembersLoading: false,
      projectMembers: [],
      guestInvitationLoading: false,
      activeTab: 0,
      guestEditDialog: false,
      projectRoleEditDialog: false,
      isGuestToOtherProjectRoleUpdate: false,
      permissionId: "",
      tags: [],
      selectedProjectRole: null,
      prevProjectRole: "",
      selectedAssignedUser: null,
      sortBy: "first_name",
      sortOrder: "asc",
      sortDesc: false,
      search: "",
      page: 1,
      itemsPerPage: 10,
      prevPage: 1,
      rows: [5, 10, 20],
      guests: [
        {
          email: "",
          first_name: "",
          last_name: "",
          tags: [],
          role: "guest",
        },
      ],
      tagOptions: ["Developer", "Designer", "Manager", "QA"],
      rules: {
        required: v => !!v || this.$vuetify.lang.t("$vuetify.validation.email_required"),
        email: (guests, currentIndex) => v => {
          const isValidFormat = !v || /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(v);
          if (!isValidFormat) return this.$vuetify.lang.t("$vuetify.validation.email_validation_error_msg_txt");

          const duplicate = guests.some((guest, i) => i !== currentIndex && guest.email === v);
          return duplicate ? this.$vuetify.lang.t("$vuetify.validation.duplicate_email") : true;
        },
      },
      tagRules: [v => (v && v.length > 0) || this.$vuetify.lang.t("$vuetify.access_tab.tag_required")],
      headers: [
        {
          text: this.$vuetify.lang.t("$vuetify.member.full_name"),
          value: "first_name",
          sortable: true,
          align: "start",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.member.email"),
          align: "start",
          sortable: true,
          value: "email",
          cellClass: "d-inline-inherit",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.member.role"),
          align: "start",
          sortable: false,
          value: "role",
          cellClass: "text-uppercase",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.member.project_role"),
          align: "center",
          sortable: false,
          value: "permissions.role",
          cellClass: "text-uppercase",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.user_access_control_permission_feature.feature_permission"),
          align: "center",
          sortable: false,
          value: "access_control",
          cellClass: "text-uppercase",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.status"),
          align: "start",
          sortable: false,
          value: "status",
          cellClass: "text-uppercase",
        },
      ],
      projectMembersHeaders: [
        {
          text: this.$vuetify.lang.t("$vuetify.member.full_name"),
          value: "user.first_name",
          sortable: true,
          align: "start",
          filterable: true,
          width: "15%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.member.email"),
          align: "start",
          sortable: true,
          value: "user.email",
          cellClass: "d-inline-inherit",
          filterable: true,
          width: "15%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.member.role"),
          align: "start",
          sortable: false,
          value: "user.role",
          cellClass: "text-uppercase",
          filterable: false,
          width: "8%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.member.project_role"),
          align: "center",
          sortable: false,
          value: "permissions.role",
          cellClass: "text-uppercase",
          filterable: false,
          width: "12%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.user_access_control_permission_feature.feature_permission"),
          align: "center",
          sortable: false,
          value: "access_control",
          cellClass: "text-uppercase",
          width: "10%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.access_tab.tags"),
          align: "start",
          sortable: false,
          value: "tags",
          cellClass: "d-inline-inherit",
          filterable: false,
          width: "18%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.status"),
          align: "start",
          sortable: false,
          value: "user.status",
          filterable: false,
          cellClass: "text-uppercase",
          width: "10%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.created_at"),
          align: "start",
          sortable: true,
          value: "created_at",
          width: "12%",
        },
      ],
      showRoleErrors: false,
      memberRole: "",
      memberStatus: "",
      memberRoleList: [
        {
          text: "ALL",
          value: "",
        },
        {
          text: "ADMIN",
          value: "admin",
        },
        {
          text: "USER",
          value: "user",
        },
        {
          text: "GUEST",
          value: "guest",
        },
      ],
      memberStatusList: [
        {
          text: "ALL",
          value: "",
        },
        {
          text: "ACTIVE",
          value: "active",
        },
        {
          text: "INACTIVE",
          value: "inactive",
        },
        {
          text: "INVITED",
          value: "invited",
        },
        {
          text: "CANCELED",
          value: "canceled",
        },
        {
          text: "EXPIRED",
          value: "expired",
        },
      ],
      memberSearch: "",
      selectedRole: "",
      selectedProjectRoleFilter: "",
      selectedStatus: "",
      pagination: {
        page: 1,
        itemsPerPage: 10,
      },
      defaultState: {
        memberSearch: "",
        selectedRole: "",
        selectedProjectRoleFilter: "",
        selectedStatus: "",
        page: 1,
        itemsPerPage: 10,
      },
      clientSideSortBy: ["created_at"],
      clientSideSortDesc: [true],
      showAccessControlModal: false,
      selectedAccessControlUser: null,
      selectedFeatureAccessUser: null,
      featureAccessForm: {
        features: [],
      },

      showFeatureAccessModal: false,
      selectedAccessUser: null,
      featureAccessConfig: {
        features: {},
      },
      isSavingFeatureAccess: false,
      selectedFeatureMember: null,
      selectedMemberFeatures: [],
      featureModalType: "",
      sortDebounce: null,
    };
  },
  computed: {
    ...mapState({
      allMembers: state => state.member.allMembers,
      selectedProject: state => state.project.selectedProject,
      projectId: state => state.project.selectedProject?.id,
      organization: state => state.account.organization,
      account: state => state.account.account,
    }),
    selectedAccessUserName() {
      if (!this.selectedAccessUser?.user) {
        return "";
      }

      const { first_name, last_name } = this.selectedAccessUser.user;

      return `${first_name || ""} ${last_name || ""}`.trim();
    },
    isDataLoaded() {
      return (
        this.account &&
        this.organization &&
        Object.keys(this.account).length > 0 &&
        Object.keys(this.organization).length > 0
      );
    },
    canViewMembers() {
      if (!this.isDataLoaded) return null;
      return this.organization.is_account_owner || this.account.show_members_to_others === true;
    },
    pageCount() {
      if (this.allMembers && this.allMembers.total) {
        const count = Math.ceil(this.allMembers.total / (this.itemsPerPage || 1));
        return count > 0 ? count : 1; // Ensure it is at least 1
      }
      return 1;
    },
    assignBtnDisable() {
      return this.selectedMembers.length === 0;
    },
    isFormValid() {
      return this.guests.every(g => g.email && g.first_name && g.last_name);
    },
    selectableMembers() {
      const unassignedMembers = this.allMembers?.items?.map(member => {
        const newMember = {
          ...member,
          selectable: !member?.is_account_owner && !this.isProjectMember(member.id),
          permissions: {
            ...member.permissions,
            role: this.userRoleSelections[member.id] || member.permissions?.role?.toLowerCase?.() || "",
          },
        };
        return newMember;
      });
      return unassignedMembers;
    },

    removeBtnDisable() {
      return this.selectedProjectMembers.length === 0;
    },
    confirmMessage() {
      return (
        this.$vuetify.lang.t("$vuetify.access_control.member_assignment_confirmation") +
        ` (${this.selectedMembers.length})`
      );
    },
    confirmRemoveMessage() {
      return this.$vuetify.lang.t("$vuetify.access_control.member_removal_confirmation");
    },
    isResetDisabled() {
      return (
        this.search === "" &&
        this.memberRole === "" &&
        this.memberStatus === "" &&
        this.sortBy === "first_name" &&
        this.sortDesc === false &&
        this.sortOrder === "asc" &&
        this.page === 1 &&
        this.itemsPerPage === 10
      );
    },
    filteredProjectMembers() {
      return this.projectMembers.filter(member => {
        const matchRole = !this.selectedRole || member.user?.role === this.selectedRole;
        const matchProjectRole =
          !this.selectedProjectRoleFilter || member.permissions?.role === this.selectedProjectRoleFilter;

        const matchStatus = !this.selectedStatus || member.user?.status === this.selectedStatus;

        return matchRole && matchProjectRole && matchStatus;
      });
    },
    isMemberResetDisabled() {
      const hasDefaultSort =
        this.clientSideSortBy.length === 1 &&
        this.clientSideSortBy[0] === "created_at" &&
        this.clientSideSortDesc.length === 1 &&
        this.clientSideSortDesc[0] === true;

      return (
        this.memberSearch === this.defaultState.memberSearch &&
        this.selectedRole === this.defaultState.selectedRole &&
        this.selectedProjectRoleFilter === this.defaultState.selectedProjectRoleFilter &&
        this.selectedStatus === this.defaultState.selectedStatus &&
        this.pagination.page === this.defaultState.page &&
        this.pagination.itemsPerPage === this.defaultState.itemsPerPage &&
        hasDefaultSort
      );
    },
  },
  watch: {
    canViewMembers: {
      handler(hasAccess) {
        if (hasAccess === null) return;
        if (hasAccess === true && this.newProjectMemberDialog) {
          this.getAllMembersFunction(this.page, this.itemsPerPage);
        }
      },
      immediate: true,
    },
    newProjectMemberDialog(val) {
      if (!val) {
        this.resetMembers();
        return;
      }
      if (this.canViewMembers === true) {
        this.getAllMembersFunction();
      }
    },
  },
  mounted() {
    this.getProjectMembers();
  },
  methods: {
    ...mapActions("member", [LOAD_ALL_MEMBERS, INVITE_GUEST_USERS]),
    ...mapActions("project", [
      ASSIGN_PROJECT_MEMBERS,
      RETRIEVE_PROJECT_MEMBERS,
      DELETE_PROJECT_MEMBERS,
      UPDATE_PROJECT_MEMBERS,
    ]),
    ...mapActions("alert", [ADD_ALERT]),
    addNewProjectMember() {
      this.newProjectMemberDialog = true;
    },
    isProjectMember(memberId) {
      return this.projectMembers.some(projectMember => projectMember.user_id === memberId);
    },
    removeGuest(index) {
      this.guests.splice(index, 1);
    },
    onMemberRoleChange(userId, newRole) {
      // below line work like, this.userRoleSelections[userId] = newRole;
      this.$set(this.userRoleSelections, userId, newRole);

      // Also update the member object in selectedMembers for immediate validation
      const memberIndex = this.selectedMembers.findIndex(m => m.id === userId);
      if (memberIndex !== -1) {
        this.$set(this.selectedMembers[memberIndex].permissions, "role", newRole);
      }

      // Clear error for this specific member when they select a role
      if (newRole && this.membersProjectRoleErrors[userId]) {
        this.$set(this.membersProjectRoleErrors, userId, false);
      }
    },
    closeMembersAssignmentModal() {
      this.newProjectMemberDialog = false;
      this.selectedMembers = [];
      this.userRoleSelections = {}; // Clear role selections
      this.membersProjectRoleErrors = {};
      this.activeTab = 0;
      this.showRoleErrors = false;
      this.resetMembers();
      this.$refs?.guestUsersForm?.resetValidation();
      this.$refs?.guestUsersForm?.reset();
      this.guests = [
        {
          email: "",
          first_name: "",
          last_name: "",
          tags: [],
          role: "guest",
        },
      ];
    },
    editUserTags(item) {
      this.guestEditDialog = true;
      this.permissionId = item.id;
      this.tags = item.tags || [];
      this.selectedProjectRole = item?.permissions?.role || null;
      this.selectedAssignedUser = { ...item };
    },
    closeGuestEditModal() {
      this.guestEditDialog = false;
      this.tags = [];
      this.permissionId = "";
      this.selectedProjectRole = null;
    },
    onUserTagsChange(val) {
      const trimmed = val.map(tag => tag.trim()).filter(tag => tag !== "");
      this.tags = Array.from(new Set(trimmed));
    },
    onProjectRoleChange(newRole, item) {
      if (newRole === "admin") {
        this.isGuestToOtherProjectRoleUpdate = true;
      }
      this.permissionId = item.id;
      this.selectedProjectRole = newRole;
      this.projectRoleEditDialog = true;
    },

    isUserProjectRole(member) {
      const projectAssignedMembers = this.$store.state.project.assignedMembers || [];
      const selectedMember = projectAssignedMembers.find(item => item.id === member.id);
      const result = selectedMember?.permissions?.role === "user";
      return result;
    },

    shouldDisableManageAccess(item, type) {
      if (type === "assign") {
        if (item?.role === "guest") {
          return true;
        } else {
          return false;
        }
      } else if (item && item?.user?.role === "guest") {
        return true;
      }
      return false;
    },

    onProjectRoleCancel() {
      // revert the change in UI
      const memberIndex = this.projectMembers.findIndex(member => member.id === this.permissionId);
      if (memberIndex !== -1 && this.prevProjectRole) {
        this.projectMembers[memberIndex].permissions.role = this.prevProjectRole; // revert
      }
      this.projectRoleEditDialog = false;
      this.prevProjectRole = ""; // reset to previous/default role
      this.permissionId = "";
      this.selectedProjectRole = null;
      this.isGuestToOtherProjectRoleUpdate = false;
    },
    onGuestsTagsChange(val, index) {
      const cleaned = Array.from(new Set(val.map(tag => tag.trim()).filter(tag => tag !== "")));
      this.guests[index].tags = cleaned;
    },
    updateProjectRole() {
      const item = this.projectMembers.find(item => item.id === this.permissionId);
      const defaultFeatures = this.$store.state.featureAccess.defaultFeatureConfig || {};
      const hasDefaultConfig = defaultFeatures && Object.keys(defaultFeatures).length > 0;
      if (!hasDefaultConfig && !item?.permissions?.features) {
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t(
              "$vuetify.user_access_control_permission_feature.feature_missing_popup_message"
            ),
            color: "error",
          },
          { root: true }
        );
        this.projectRoleEditDialog = false;
        return;
      }
      const featuresPayload =
        this.selectedProjectRole === "user"
          ? { features: extractFeaturePermissions(item?.permissions?.features || defaultFeatures) || {} }
          : {};
      let payload = {
        permissions: {
          role: this.selectedProjectRole,
          ...featuresPayload,
        },
      };
      this.UPDATE_PROJECT_MEMBERS({
        projectId: this.selectedProject.id,
        payload,
        permission_id: this.permissionId,
      })
        .then(_res => {
          this.permissionId = "";
          this.getProjectMembers();
          this.prevProjectRole = "";
          this.isGuestToOtherProjectRoleUpdate = false;
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.access_tab.project_role_updated_successfully"),
              color: "success",
            },
            { root: true }
          );
        })
        .catch(err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: err.response?.data?.message,
              color: "error",
            },
            { root: true }
          );
          console.error("Error", err);
        })
        .finally(() => {
          this.projectRoleEditDialog = false;
        });
    },

    updateGuestUsersTag() {
      this.isLoading = true;
      let payload = { tags: this.tags };

      this.UPDATE_PROJECT_MEMBERS({
        projectId: this.selectedProject.id,
        payload,
        permission_id: this.permissionId,
      })
        .then(_res => {
          this.guestEditDialog = false;
          this.permissionId = "";
          this.getProjectMembers();
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.access_tab.tags_updated_successfully"),
              color: "success",
            },
            { root: true }
          );
        })
        .catch(err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: err.response?.data?.message,
              color: "error",
            },
            { root: true }
          );
          console.error("Error", err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    removeMembersTag(item) {
      this.tags.splice(this.tags.indexOf(item), 1);
    },
    inviteGuestMember() {
      const valid = this.$refs.guestUsersForm.validate();
      if (!valid) return;
      this.guestInvitationLoading = true;
      this.INVITE_GUEST_USERS({
        projectId: this.selectedProject.id,
        invitations: this.guests,
      })
        .then(res => {
          if (res?.status === 201) {
            this.getProjectMembers();
            this.newProjectMemberDialog = false;
            this.guests = [
              {
                email: "",
                first_name: "",
                last_name: "",
                tags: [],
                role: "guest",
              },
            ];
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t("$vuetify.access_tab.guest_successfully_added_msg"),
                color: "success",
              },
              { root: true }
            );
          }
        })
        .catch(e => {
          console.error("Error", e);
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: e.response?.data?.message,
              color: "error",
            },
            { root: true }
          );
        })
        .finally(() => {
          this.guestInvitationLoading = false;
        });
    },
    showMemberAssignmentConfirmationModal() {
      // Reset all previous errors
      this.membersProjectRoleErrors = {};

      let hasInvalidRoles = false;

      // Check each selected member individually
      this.selectedMembers.forEach(member => {
        const hasError = !member.permissions.role;

        // Set individual error state
        this.$set(this.membersProjectRoleErrors, member.id, hasError);

        if (hasError) {
          hasInvalidRoles = true;
        }
      });

      if (hasInvalidRoles) {
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t("$vuetify.access_control.project_role_required_error_message"),
            color: "error",
          },
          { root: true }
        );
        return;
      }

      // If all valid, proceed with confirmation
      this.memberConfirmationModal = true;
      this.newProjectMemberDialog = false;
    },
    removeTag(item, index) {
      this.guests[index].tags.splice(this.guests[index].tags.indexOf(item), 1);
    },
    isProjectOwner(id) {
      if (this.selectedProject.owner?.id === id) {
        return true;
      }
      return false;
    },
    addGuest() {
      this.guests.push({
        email: "",
        first_name: "",
        last_name: "",
        tags: [],
        role: "guest",
      });
    },
    getAllMembersFunction({
      start = this.page,
      size = this.itemsPerPage,
      search = this.search,
      role = this.memberRole,
      status = this.memberStatus,
      sortBy = this.sortBy === "first_name" ? "full_name" : this.sortBy,
      sortOrder = this.sortOrder,
    } = {}) {
      this.isLoading = true;
      this.LOAD_ALL_MEMBERS({ start, size, search, role, status, sortBy, sortOrder, projectId: this.projectId }).then(
        _response => {
          this.isLoading = false;
        },
        _error => {
          this.isLoading = false;
        }
      );
    },
    removeProjectMembers() {
      this.assignedMembersLoading = true;
      const membersToBeRemoved = this.selectedProjectMembers.map(user => {
        return user.user_id;
      });
      const previousMembers = [...this.projectMembers];

      this.DELETE_PROJECT_MEMBERS({
        project_id: this.projectId,
        users: {
          user_ids: membersToBeRemoved,
        },
      })
        .then(res => {
          if (res?.status === 200) {
            const filteredMembers = previousMembers.filter(member => {
              return !membersToBeRemoved.some(removedMember => {
                return removedMember === member.user_id;
              });
            });
            this.projectMembers = filteredMembers;
            this.selectedProjectMembers = [];
            this.getAllMembersFunction();
          }
        })
        .catch(err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.access_control.member_removal_error"),
              color: "error",
            },
            { root: true }
          );
          console.error("Err", err);
        })
        .finally(() => {
          this.memberRemoveConfirmationModal = false;
          this.assignedMembersLoading = false;
        });
    },
    assignMembers() {
      this.memberConfirmationModal = false;
      this.assignedMembersLoading = true;
      const defaultFeatures = this.$store.state.featureAccess.defaultFeatureConfig || {};
      const hasDefaultConfig = defaultFeatures && Object.keys(defaultFeatures).length > 0;
      if (!hasDefaultConfig) {
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t(
              "$vuetify.user_access_control_permission_feature.feature_missing_popup_message"
            ),
            color: "error",
          },
          { root: true }
        );
        this.assignedMembersLoading = false;
        return;
      }
      const mappedSelectedMembers = this.selectedMembers.map(user => {
        const selectedFeature = this.selectedMemberFeatures.find(item => item.permission_id === user.id);
        let permissions = {
          ...user.permissions,
          role: user?.permissions?.role?.toLowerCase?.(),
          ...(user?.permissions?.role?.toLowerCase?.() === "user" && {
            features: extractFeaturePermissions(selectedFeature?.features || defaultFeatures),
          }),
        };

        const newUser = { user_id: user.id, permissions };
        return newUser;
      });

      const users = { user_assignments: mappedSelectedMembers };
      this.ASSIGN_PROJECT_MEMBERS({ project_id: this.projectId, users })
        .then(res => {
          if (res?.status === 201) {
            this.getProjectMembers();
            this.selectedMembers = [];
            this.getAllMembersFunction();
          }
        })
        .catch(err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message:
                err?.response?.data?.message || this.$vuetify.lang.t("$vuetify.access_control.member_assignment_error"),
              color: "error",
            },
            { root: true }
          );
          console.error("Error", err);
        })
        .finally(() => {
          this.assignedMembersLoading = false;
        });
    },
    getProjectMembers() {
      this.assignedMembersLoading = true;
      this.RETRIEVE_PROJECT_MEMBERS({
        project_id: this.projectId,
      })
        .then(res => {
          this.projectMembers = res.data?.members.map(member => {
            const newMember = {
              ...member,
              permissions: {
                ...member.permissions,
                role: member.permissions?.role,
              },
            };
            return newMember;
          });
        })
        .catch(err => {
          console.error("Err", err);
        })
        .finally(() => {
          this.assignedMembersLoading = false;
        });
    },
    //fixme: need to remove this function, // can view members if not belongs to this account, if belongs than has to be the owner
    resetToFirstPage() {
      this.page = 1;
      this.prevPage = 1;
    },
    handleQueryOnMemberList() {
      this.resetToFirstPage();
      this.getAllMembersFunction();
    },
    handleSearch() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.resetToFirstPage();
        this.getAllMembersFunction();
      }, 500);
    },
    onSortChange() {
      this.resetToFirstPage();
      clearTimeout(this.sortDebounce);
      this.sortDebounce = setTimeout(() => {
        this.sortOrder = this.sortDesc ? "desc" : "asc";
        this.getAllMembersFunction();
      }, 0);
    },

    resetMembers() {
      this.search = "";
      this.memberRole = "";
      this.memberStatus = "";

      this.sortBy = "first_name";
      this.sortDesc = false;
      this.sortOrder = "asc";

      this.itemsPerPage = 10;
      this.resetToFirstPage();
    },

    resetAndFetchMembers() {
      this.resetMembers();
      this.getAllMembersFunction();
    },
    onItemsPerPageChange() {
      this.resetToFirstPage();
      this.getAllMembersFunction();
    },
    onPageChange(value) {
      if (value === this.prevPage) return;
      this.prevPage = value;
      this.getAllMembersFunction();
    },
    resetFilters() {
      this.memberSearch = "";
      this.selectedRole = "";
      this.selectedProjectRoleFilter = "";
      this.selectedStatus = "";
      this.pagination.page = 1;
      this.pagination.itemsPerPage = 10;
      this.clientSideSortBy = ["created_at"];
      this.clientSideSortDesc = [true];
    },
    customSearch(value, search, item) {
      if (!search) return true;
      search = search.toLowerCase();

      const first = item.user?.first_name?.toLowerCase() || "";
      const last = item.user?.last_name?.toLowerCase() || "";
      const full = (first + " " + last).trim();
      const email = item.user?.email?.toLowerCase() || "";

      return first.includes(search) || last.includes(search) || full.includes(search) || email.includes(search);
    },
    closeAccessControlModal() {
      this.showAccessControlModal = false;
      this.selectedAccessControlUser = null;
      this.featureAccessConfig = {
        features: {},
      };
    },
    onFeatureToggle(feature) {
      /*
       * Optional behavior:
       * disabling a parent feature also disables all its sections.
       */
      if (!feature.enabled && Array.isArray(feature.sections)) {
        feature.sections.forEach(section => {
          section.enabled = false;
        });
      }
    },
    resetFeatureAccessState() {
      this.selectedAccessUser = null;
      this.permissionId = "";
      this.selectedProjectRole = null;
      this.featureAccessConfig = {
        features: {},
      };
    },

    openFeatureAccessModal(item, type) {
      this.featureModalType = type;
      this.selectedAccessUser = item;
      this.permissionId = item.id;
      if (type === "assign") {
        const selectedFeature = this.selectedMemberFeatures.find(feature => feature.permission_id === item.id);
        this.featureAccessConfig = {
          features: JSON.parse(
            JSON.stringify(selectedFeature?.features || this.$store.state.featureAccess?.defaultFeatureConfig || {})
          ),
        };
        this.showFeatureAccessModal = true;
        return;
      }
      this.selectedProjectRole = item.permissions?.role;
      const defaultFeatures = this.$store.state.featureAccess?.defaultFeatureConfig || {};
      const userFeaturesData =
        item?.permissions?.features && Object.keys(item.permissions.features).length > 0
          ? mergeFeatureConfig(item.permissions.features, defaultFeatures)
          : defaultFeatures;
      const userFeatures = userFeaturesData;
      this.featureAccessConfig = {
        features: JSON.parse(JSON.stringify(userFeatures)),
      };
      this.showFeatureAccessModal = true;
    },
    updateMembersFeatureAccess(memberId, features) {
      const memberIndex = this.projectMembers.findIndex(member => member.id === memberId);
      if (memberIndex !== -1) {
        this.$set(this.projectMembers[memberIndex].permissions, "features", features || {});
      }
    },
    async saveFeatureAccess(updatedFeatureConfig) {
      if (this.featureModalType === "assign") {
        const index = this.selectedMemberFeatures.findIndex(item => item.permission_id === this.selectedAccessUser.id);

        const payload = {
          permission_id: this.selectedAccessUser.id,
          features: updatedFeatureConfig.features,
        };

        if (index > -1) {
          this.selectedMemberFeatures.splice(index, 1, payload);
        } else {
          this.selectedMemberFeatures.push(payload);
        }

        this.showFeatureAccessModal = false;
        return;
      }
      if (!this.selectedAccessUser || !this.permissionId) {
        return;
      }

      this.isSavingFeatureAccess = true;
      const defaultFeatures = this.$store.state.featureAccess.defaultFeatureConfig || {};
      const updatedFeaturePermission = extractFeaturePermissions(updatedFeatureConfig?.features || defaultFeatures);
      const payload = {
        permissions: {
          role: this.selectedProjectRole || this.selectedAccessUser?.permissions?.role,
          features: updatedFeaturePermission,
        },
      };
      try {
        const res = await this.UPDATE_PROJECT_MEMBERS({
          projectId: this.selectedProject.id,
          payload,
          permission_id: this.permissionId,
        });

        this.permissionId = "";
        this.selectedProjectRole = "";
        this.prevProjectRole = "";
        this.isGuestToOtherProjectRoleUpdate = false;

        this.showFeatureAccessModal = false;
        this.updateMembersFeatureAccess(this.selectedAccessUser.id, updatedFeaturePermission);
        this.selectedAccessUser = null;
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message:
              res?.data?.message || this.$vuetify.lang.t("$vuetify.access_control.feature_access_updated_successfully"),
            color: "success",
          },
          { root: true }
        );
      } catch (err) {
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: err?.response?.data?.message || err?.message || err,
            color: "error",
          },
          { root: true }
        );

        console.error("Feature access update error:", err);
      } finally {
        this.isSavingFeatureAccess = false;
      }
    },

    closeFeatureAccessModal() {
      this.showFeatureAccessModal = false;
      this.isSavingFeatureAccess = false;
      this.resetFeatureAccessState();
    },
  },
};
</script>

<style scoped>
.error-border {
  color: #ff5252;
  /* border: 2px solid #ff5252 !important; */
  /* border-radius: 4px; */
}
.reset-btn {
  border-color: #d6d6d6 !important;
  color: #616161 !important;
}
.fixed-dialog {
  height: 800px;
  overflow-y: auto;
}
@media (max-width: 959px) {
  .members-table .v-data-table-header-mobile .v-chip .v-icon {
    opacity: 1 !important;
    visibility: visible !important;
    display: inline-flex !important;
  }
}
</style>
